import { add, dot, scale, sub, type Vec3 } from '@rcs/domain';

/**
 * 光線（`origin` から `direction` 方向）と、平面（通る点 `planePoint`、法線 `planeNormal`）の交点。
 * 平行、または交点が光線の後ろ側なら `undefined`。
 */
export function rayPlaneIntersection(
  origin: Vec3,
  direction: Vec3,
  planePoint: Vec3,
  planeNormal: Vec3,
): Vec3 | undefined {
  const denominator = dot(direction, planeNormal);
  if (Math.abs(denominator) < 1e-9) return undefined;
  const t = dot(sub(planePoint, origin), planeNormal) / denominator;
  if (t < 0) return undefined;
  return add(origin, scale(direction, t));
}

/** 画面上の候補点（ピクセル座標）。 */
export interface ScreenCandidate {
  readonly id: string;
  readonly x: number;
  readonly y: number;
}

/**
 * 画面上の位置 `(x, y)` に最も近い候補の ID。`radius` ピクセルより遠いものは対象外。
 * 距離が同じなら、先に並んでいるものを選ぶ。
 */
export function pickNearest(
  candidates: readonly ScreenCandidate[],
  x: number,
  y: number,
  radius: number,
): string | undefined {
  let best: string | undefined;
  let bestDistance = radius;
  for (const candidate of candidates) {
    const distance = Math.hypot(candidate.x - x, candidate.y - y);
    if (distance <= bestDistance && (best === undefined || distance < bestDistance)) {
      best = candidate.id;
      bestDistance = distance;
    }
  }
  return best;
}

/** 選択を当てる半径 [px]。指（タッチ）はマウスより大きくとる（NFR-USA-002）。 */
export function pickRadius(pointerType: string): number {
  return pointerType === 'touch' || pointerType === 'pen' ? 28 : 14;
}

/**
 * 制御点の目印の大きさ [m]。カメラからの距離に比例させ、画面上の大きさをほぼ一定にする。
 * 選択中は大きくする（色だけに頼らず、大きさでも区別する）。
 */
export function markerRadius(distanceToCamera: number, selected: boolean): number {
  const base = Math.max(0.25, distanceToCamera * 0.011);
  return selected ? base * 1.6 : base;
}

/**
 * 制御点をドラッグするときの、移動面の選び方。移動の制約（`@rcs/editor` の `MoveConstraint`）と同じ名前。
 *
 * - `free`：カメラに正対する平面
 * - `horizontal`：水平面（高さ一定）
 * - `x` / `y` / `z`：その軸を含み、カメラにできるだけ正対する平面
 */
export type DragPlane = 'free' | 'horizontal' | 'x' | 'y' | 'z';

/**
 * 移動面の法線。`look` はカメラの視線の向き（単位ベクトル）。
 * 軸を含む平面の法線は、視線から軸方向の成分を取り除いたもの。視線が軸とほぼ平行で、
 * 平面が決まらないときは、カメラに正対する平面に戻す。
 */
export function dragPlaneNormal(plane: DragPlane, look: Vec3): Vec3 {
  if (plane === 'free') return look;
  if (plane === 'horizontal') return [0, 1, 0];
  const axis: Vec3 = plane === 'x' ? [1, 0, 0] : plane === 'y' ? [0, 1, 0] : [0, 0, 1];
  const along = dot(look, axis);
  const raw = sub(look, scale(axis, along));
  const length = Math.hypot(raw[0], raw[1], raw[2]);
  return length < 1e-3 ? look : scale(raw, 1 / length);
}
